if (!customElements.get("recommended-product-form")) {
  customElements.define(
    "recommended-product-form",
    class RecommendedProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantSelects = this.querySelector("variant-selects");
        this.variantIdInput = this.form?.querySelector("[name=id]");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector("span");

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        if (document.querySelector("cart-drawer")) {
          this.submitButton?.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      connectedCallback() {
        // Listen for variant selection changes
        if (this.variantSelects) {
          this.variantSelects.addEventListener(
            "change",
            this.onVariantChange.bind(this),
          );
        }

        // Handle form submission
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));

        // Initialize with current variant data
        this.updateVariantAvailability();
      }

      onVariantChange(event) {
        const selectedOptions = this.getSelectedOptions();
        const matchedVariant = this.findMatchingVariant(selectedOptions);

        if (matchedVariant) {
          this.updateVariantInput(matchedVariant.id);
          this.updateSubmitButton(matchedVariant.available);
          this.updatePrice(matchedVariant);
        } else {
          this.setUnavailable();
        }
      }

      updatePrice(variant) {
        const cardWrapper = this.closest(".card-wrapper");
        if (!cardWrapper) return;

        const priceElement = cardWrapper.querySelector(
          ".recommended-product-price .price__container",
        );
        const dollarlabsPrice = cardWrapper.querySelector(
          "[data-custom-price]",
        );

        const moneyPrice = this.formatMoney(variant.price);
        const compareAtPrice = variant.compare_at_price;

        // First try to update via dollarlabs-price if it exists (for is_collection: true)
        if (dollarlabsPrice) {
          const productId = dollarlabsPrice.dataset.productId;
          if (productId) {
            // Update the data attributes for dollarlabs to pick up
            dollarlabsPrice.dataset.variantId = variant.id;
            dollarlabsPrice.dataset.originalPrice = variant.price;

            // Update the displayed price directly
            const regularPriceEl = dollarlabsPrice.querySelector(
              ".dollarlabs_pricelist--regular_price",
            );
            const finalPriceEl = dollarlabsPrice.querySelector(
              ".dollarlabs_pricelist--final_price",
            );

            if (regularPriceEl) {
              regularPriceEl.textContent = moneyPrice;
            }
            if (finalPriceEl) {
              finalPriceEl.textContent = moneyPrice;
            }

            // Handle compare at price / strikeout
            const originalPriceEl = dollarlabsPrice.querySelector(
              ".dollarlabs_pricelist--original_price",
            );
            if (originalPriceEl) {
              if (compareAtPrice && compareAtPrice > variant.price) {
                originalPriceEl.style.display = "inline";
                originalPriceEl.textContent = this.formatMoney(compareAtPrice);
              } else {
                originalPriceEl.style.display = "none";
              }
            }
          }
        }

        // Fallback: Update standard price elements if dollarlabs not present
        if (priceElement) {
          const regularPriceElement = priceElement.querySelector(
            ".price__regular .price-item--regular",
          );
          const salePriceElement = priceElement.querySelector(
            ".price__sale .price-item--sale",
          );

          if (regularPriceElement) {
            regularPriceElement.textContent = moneyPrice;
          }
          if (salePriceElement) {
            salePriceElement.textContent = moneyPrice;
          }

          // Update compare at price if it exists
          if (compareAtPrice && compareAtPrice > variant.price) {
            const compareAtPriceElement = priceElement.querySelector(
              ".price__regular s, .price__sale s",
            );
            if (compareAtPriceElement) {
              compareAtPriceElement.classList.remove("hidden");
              compareAtPriceElement.textContent =
                this.formatMoney(compareAtPrice);
            }

            // Update sale styling
            const priceContainer = priceElement.closest(".price");
            if (priceContainer) {
              priceContainer.classList.add("price--on-sale");
            }
          } else {
            const compareAtPriceElement = priceElement.querySelector(
              ".price__regular s, .price__sale s",
            );
            if (compareAtPriceElement) {
              compareAtPriceElement.classList.add("hidden");
            }

            // Remove sale styling
            const priceContainer = priceElement.closest(".price");
            if (priceContainer) {
              priceContainer.classList.remove("price--on-sale");
            }
          }

          // Update sold out styling
          const priceContainer = priceElement.closest(".price");
          if (priceContainer) {
            if (!variant.available) {
              priceContainer.classList.add("price--sold-out");
            } else {
              priceContainer.classList.remove("price--sold-out");
            }
          }
        }
      }

      formatMoney(cents) {
        const moneyFormat = window.shopify?.money_format || "${{amount}}";
        const amount = (cents / 100).toFixed(2);
        return moneyFormat
          .replace("{{amount}}", amount)
          .replace("{{amount_no_decimals}}", Math.round(amount));
      }

      getSelectedOptions() {
        const selects = this.variantSelects?.querySelectorAll("select");
        if (!selects) return [];

        return Array.from(selects).map((select) => ({
          name: select.name.replace("options[", "").replace("]", ""),
          value: select.value,
        }));
      }

      findMatchingVariant(selectedOptions) {
        const variantsData = this.getVariantsData();
        if (!variantsData || !variantsData.length) return null;

        return variantsData.find((variant) => {
          return selectedOptions.every((option) => {
            const variantOption = variant.options.find(
              (vo) => vo.name === option.name,
            );
            return variantOption && variantOption.value === option.value;
          });
        });
      }

      getVariantsData() {
        const dataElement = this.querySelector("[data-variants]");
        if (!dataElement) return null;

        try {
          return JSON.parse(dataElement.textContent);
        } catch (e) {
          console.error("Error parsing variants data:", e);
          return null;
        }
      }

      updateVariantInput(variantId) {
        if (this.variantIdInput) {
          this.variantIdInput.value = variantId;
          this.variantIdInput.disabled = false;
        }
      }

      updateSubmitButton(available) {
        if (!this.submitButton) return;

        if (available) {
          this.submitButton.removeAttribute("disabled");
          this.submitButton.setAttribute("aria-disabled", "false");

          const soldOutMessage =
            this.submitButton.querySelector(".sold-out-message");
          if (soldOutMessage) {
            soldOutMessage.classList.add("hidden");
          }
        } else {
          this.submitButton.setAttribute("disabled", "disabled");
          this.submitButton.setAttribute("aria-disabled", "true");

          const soldOutMessage =
            this.submitButton.querySelector(".sold-out-message");
          if (soldOutMessage) {
            soldOutMessage.classList.remove("hidden");
          }
        }
      }

      setUnavailable() {
        if (this.variantIdInput) {
          this.variantIdInput.value = "";
          this.variantIdInput.disabled = true;
        }
        this.updateSubmitButton(false);
      }

      updateVariantAvailability() {
        if (!this.variantIdInput || !this.variantIdInput.value) return;

        const variantsData = this.getVariantsData();
        if (!variantsData) return;

        const currentVariant = variantsData.find(
          (v) => v.id.toString() === this.variantIdInput.value,
        );

        if (currentVariant) {
          this.updateSubmitButton(currentVariant.available);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner")?.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "recommended-product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText?.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "recommended-product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner")?.classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
